/**
 * TournamentsProvider — injeta `SupabaseClient` + `userId` para os hooks de
 * torneios consumirem via context (mirror de `MutationsProvider` em
 * `@pps/mutations/src/MutationsProvider.tsx`).
 *
 * Uso (uma única vez no app root):
 *
 *   import { TournamentsProvider } from "@pps/tournaments-hooks";
 *   import { supabase } from "@/integrations/supabase/client";
 *   import { useAuth } from "@/contexts/AuthContext";
 *
 *   function Root() {
 *     const { user } = useAuth();
 *     return (
 *       <TournamentsProvider client={supabase} userId={user?.id ?? null}>
 *         <App />
 *       </TournamentsProvider>
 *     );
 *   }
 *
 * Wave 1a: apenas skeleton + hook acessor. Os hooks de domínio
 * (useLiveMatch, useTournamentBracket, useHeadToHead, usePlayerRecentMatches,
 * useTournamentTV) serão movidos em Wave 1b e passarão a chamar
 * `useTournamentsSupabase()` em vez de `import "@/integrations/supabase/client"`.
 *
 * `playerHref` existe pelo mesmo motivo do `client`: o pacote não pode assumir
 * as rotas de quem o consome. Os componentes de torneio nasceram em
 * `apps/public`, que tem `/jogador/:userId`; o CRM não tem essa rota e o link
 * hardcoded caía no catch-all `path="*"` (404). Quem monta o provider é quem
 * sabe se existe página de jogador — o portal passa o resolver, o CRM omite
 * de propósito e o nome vira texto.
 *
 * `liveMatchHref` é o mesmo problema na outra ponta: o botão "Ao vivo" do card
 * navegava para `/partida/:matchId/live`, que só o portal declara. O CRM tem a
 * página equivalente em outro endereço (`/torneios/:tournamentId/partidas/:matchId`),
 * e por isso o resolver recebe também o torneio — é o consumidor que sabe do
 * que sua rota precisa.
 */
import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@pps/supabase";

export type TournamentsSupabaseClient = SupabaseClient<Database>;

interface TournamentsContextValue {
  client: TournamentsSupabaseClient;
  userId: string | null;
  playerHref: ((playerId: string) => string) | null;
  liveMatchHref:
    | ((matchId: string, tournamentId?: string | null) => string | null)
    | null;
}

const TournamentsContext = createContext<TournamentsContextValue | null>(null);

export interface TournamentsProviderProps {
  children: ReactNode;
  client: TournamentsSupabaseClient;
  userId: string | null;
  /**
   * Rota do perfil do jogador no app consumidor. Omitir quando o app não tem
   * página de jogador — aí o nome do atleta é renderizado como texto.
   */
  playerHref?: (playerId: string) => string;
  /**
   * Rota do placar ao vivo da partida no app consumidor. O portal resolve só
   * com o `matchId`; o CRM precisa também do torneio. Omitir quando o app não
   * tem essa página — aí o botão "Ao vivo" simplesmente não é oferecido.
   */
  liveMatchHref?: (
    matchId: string,
    tournamentId?: string | null,
  ) => string | null;
}

export function TournamentsProvider({
  children,
  client,
  userId,
  playerHref,
  liveMatchHref,
}: TournamentsProviderProps) {
  const value = useMemo<TournamentsContextValue>(
    () => ({
      client,
      userId,
      playerHref: playerHref ?? null,
      liveMatchHref: liveMatchHref ?? null,
    }),
    [client, userId, playerHref, liveMatchHref],
  );
  return (
    <TournamentsContext.Provider value={value}>
      {children}
    </TournamentsContext.Provider>
  );
}

/**
 * Hook acessor — usado pelos hooks de domínio (Wave 1b+) para puxar o
 * client e o userId injetados pelo Provider. Lança erro se chamado fora
 * de um `<TournamentsProvider>`.
 */
export function useTournamentsSupabase(): TournamentsContextValue {
  const ctx = useContext(TournamentsContext);
  if (ctx === null) {
    throw new Error(
      "useTournamentsSupabase must be called inside <TournamentsProvider>.",
    );
  }
  return ctx;
}

/**
 * Resolver da rota de perfil do jogador, ou `null` quando o app consumidor
 * não tem essa página (CRM) — e também quando não há provider nenhum.
 *
 * Diferente de `useTournamentsSupabase`, este hook NÃO lança fora do provider:
 * um componente de apresentação renderizado isolado (storybook, teste de
 * unidade) deve degradar para texto em vez de quebrar a árvore.
 */
export function usePlayerHref(): ((playerId: string) => string) | null {
  return useContext(TournamentsContext)?.playerHref ?? null;
}

/**
 * Resolver da rota do placar ao vivo, ou `null` quando o app consumidor não
 * tem essa página — e também quando não há provider nenhum. Componente que
 * oferece o botão "Ao vivo" deve escondê-lo quando isto for `null`, em vez de
 * navegar para um endereço que não existe.
 *
 * Não lança fora do provider, pelo mesmo motivo de `usePlayerHref`.
 */
export function useLiveMatchHref():
  | ((matchId: string, tournamentId?: string | null) => string | null)
  | null {
  return useContext(TournamentsContext)?.liveMatchHref ?? null;
}
